/**
 * Passos de um job de gateway na ordem do `provisioner` (spec 2026-10-15 §4.3), com os
 * rótulos pt-BR e a frase de cada código de erro. Um arquivo só: o teste
 * `passosDoJob.test.ts` confere estas tuplas contra as do
 * `src/consoleai/whatsapp_gateways/provisioner.py` copiadas nele.
 */
export type KindDoJob = 'provision' | 'deprovision' | 'update'
export type StatusDoJob = 'pending' | 'running' | 'success' | 'failed'

export const ROTULO_KIND: Record<KindDoJob, string> = { provision: 'Provisionar', deprovision: 'Remover', update: 'Atualizar' }
export const VERBO_DO_KIND: Record<KindDoJob, string> = { provision: 'provisionar', deprovision: 'remover', update: 'atualizar' }
export const ROTULO_STATUS_DO_JOB: Record<StatusDoJob, string> = {
  pending: 'na fila', running: 'em andamento', success: 'concluída', failed: 'falhou',
}

export const PASSOS: Record<KindDoJob, readonly string[]> = {
  provision: ['segredos', 'access_point', 'aguardando_access_point', 'imagem', 'task_definition',
    'servico', 'aguardando_gateway', 'redistribuir_whatsapi', 'aguardando_whatsapi', 'sync'],
  deprovision: ['sync_remover', 'servico', 'aguardando_servico', 'task_definition', 'access_point', 'segredos'],
  update: ['imagem', 'task_definition', 'servico', 'aguardando_gateway', 'sessao', 'registrar_versao'],
}
/** Fora da sequência: só entra quando o rollout do `update` falha e o ECS volta. */
export const PASSO_ROLLBACK = 'aguardando_rollback'

// O mesmo nome de passo quer dizer coisa diferente em cada kind (`servico` cria ou apaga).
export const ROTULO_PASSO: Record<KindDoJob, Record<string, string>> = {
  provision: {
    segredos: 'Criar os segredos', access_point: 'Criar o espaço da sessão',
    aguardando_access_point: 'Esperar o espaço da sessão', imagem: 'Conferir a imagem',
    task_definition: 'Registrar a tarefa', servico: 'Subir o serviço',
    aguardando_gateway: 'Esperar o gateway subir', redistribuir_whatsapi: 'Avisar o WhatsApi',
    aguardando_whatsapi: 'Esperar o WhatsApi', sync: 'Registrar no WhatsApi',
  },
  deprovision: {
    sync_remover: 'Tirar do WhatsApi', servico: 'Apagar o serviço',
    aguardando_servico: 'Esperar o serviço sair', task_definition: 'Desregistrar a tarefa',
    access_point: 'Apagar o espaço da sessão', segredos: 'Apagar os segredos',
  },
  update: {
    imagem: 'Conferir a imagem', task_definition: 'Registrar a tarefa nova',
    servico: 'Trocar a tarefa do serviço', aguardando_gateway: 'Esperar o gateway subir',
    sessao: 'Conferir a sessão', registrar_versao: 'Registrar a versão',
    [PASSO_ROLLBACK]: 'Esperar o ECS voltar à versão anterior',
  },
}

/** Frase do código gravado por `_codigo(exc)`. Descreve o código; não diagnostica. */
const FRASE_DE_ERRO: Record<string, string> = {
  rollout_failed: 'O serviço subiu e caiu — a última implantação falhou',
  rollback_failed: 'O ECS não conseguiu voltar à versão anterior',
  EsperaExcedida: 'Passou de 15 min esperando',
  AccessDeniedException: 'Falta permissão na AWS — avise o DevOps',
  AccessDenied: 'Falta permissão na AWS — avise o DevOps',
  pedido_whatsapi_em_ausente: 'O pedido ao WhatsApi se perdeu do job',
  access_point_error: 'A AWS não conseguiu criar o espaço da sessão',
  gateway_in_use: 'Há chave de API apontada para este gateway',
  InfraNaoEncontrada: 'A infraestrutura dos gateways não está como o provisionamento espera',
  sessao_perdida: 'O gateway subiu sem a sessão pareada',
  deployment_id_ausente: 'O ECS não devolveu a implantação pedida',
  ImageNotFoundException: 'A imagem desta versão não está no ECR',
}

/** A frase (quando o código é conhecido) e o código cru, separados: a frase é texto,
 *  o código é dado e vai em monospace (DS §3.4). */
export function partesDoErro(codigo: string | null | undefined): { frase: string | null; codigo: string } | null {
  if (!codigo) return null
  // `last_error` vem como `passo: código`; o do passo vem só com o código.
  const cru = codigo.includes(': ') ? codigo.slice(codigo.indexOf(': ') + 2) : codigo
  return { frase: FRASE_DE_ERRO[cru] ?? null, codigo: cru }
}

export function fraseDoErro(codigo: string | null | undefined): string | null {
  const p = partesDoErro(codigo)
  if (!p) return null
  return p.frase ? `${p.frase} (${p.codigo})` : p.codigo
}

const ROTULO_FORA_DA_SEQUENCIA: Record<string, string> = { infra: 'Preparar o acesso à AWS' }

export function rotuloDoPasso(kind: KindDoJob, passo: string): string {
  return ROTULO_PASSO[kind][passo] ?? ROTULO_FORA_DA_SEQUENCIA[passo] ?? passo
}

// Mesma regra de `provisioner.passo_de_retomada`: a espera volta para a ação que ela espera,
// e com a versão trocada quem passou da imagem volta para ela. O backend decide; isto só
// diz na confirmação de onde vai recomeçar.
const RETOMADA_DA_ESPERA: Record<string, string> = {
  aguardando_access_point: 'access_point', aguardando_gateway: 'servico',
  aguardando_whatsapi: 'redistribuir_whatsapi',
}

export function passoDeRetomada(step: string | null, versaoMudou: boolean): string {
  const ordem = PASSOS.provision
  if (!step || !ordem.includes(step)) return ordem[0]
  const passo = RETOMADA_DA_ESPERA[step] ?? step
  if (versaoMudou && ordem.indexOf(passo) > ordem.indexOf('imagem')) return 'imagem'
  return passo
}
